import type { TeamRole } from '@antoja/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { t } from '../i18n';
import type { ConsoleApi } from '../lib/api';
import { Button, CenteredPage } from '../ui';
import { LoginScreen } from './login-screen';

export interface TeamSession {
  readonly email: string;
  readonly role: TeamRole;
  /** Signed in through a "create your password" link: the account screen says so. */
  readonly recovery: boolean;
}

/**
 * Lets in only active team members, using the database itself (team_members and its
 * RLS). This is the real boundary: Cloudflare Access is planned in front of the console
 * (before any real personal data or a production deploy) but is not set up yet.
 */
export function AuthGate({
  api,
  children,
}: {
  api: ConsoleApi;
  children: (session: TeamSession) => ReactNode;
}) {
  const [email, setEmail] = useState<string | null | undefined>(undefined);
  const [recovery, setRecovery] = useState(false);

  useEffect(() => {
    let active = true;
    void api.getSessionEmail().then(
      (value) => active && setEmail(value),
      () => active && setEmail(null),
    );
    const unsubscribe = api.onSessionChange((value, fromRecoveryLink) => {
      setEmail(value);
      if (fromRecoveryLink) {
        setRecovery(true);
        window.location.hash = '#/cuenta'; // straight to "create your password"
      }
      if (value === null) setRecovery(false);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [api]);

  const role = useQuery({
    queryKey: ['team-role', email],
    queryFn: () => api.getMyRole(),
    enabled: typeof email === 'string',
  });

  if (email === undefined || (email && role.isPending)) {
    return (
      <CenteredPage>
        <p className="text-muted" role="status">
          {t('auth.loading')}
        </p>
      </CenteredPage>
    );
  }
  if (email === null) return <LoginScreen api={api} />;

  if (role.isError) {
    return (
      <CenteredPage>
        <p role="alert">{t('auth.error')}</p>
        <Button className="justify-self-start" onClick={() => void role.refetch()}>
          {t('common.retry')}
        </Button>
      </CenteredPage>
    );
  }
  if (!role.data) {
    return (
      <CenteredPage>
        <div className="grid gap-2">
          <h1 className="text-[28px] leading-tight font-semibold tracking-[-0.03em]">
            {t('auth.denied.title')}
          </h1>
          <p className="text-muted">{t('auth.denied.body', { email })}</p>
        </div>
        <Button className="justify-self-start" onClick={() => void api.signOut()}>
          {t('auth.signOut')}
        </Button>
      </CenteredPage>
    );
  }
  return <>{children({ email, role: role.data, recovery })}</>;
}
